import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import {
  Input,
  PopoverBody,
  UncontrolledPopover,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  getCollection,
  addSelectedCollection,
  removeSelectedCollection,
} from '../../actions/Collections';

import CollectionsActions from './Actions';
import CollectionsAccess from './Access';
import GridItemLikes from '../GridItemLikes';
import Avatar from '../Avatar';
import CollectionsDate from './Date';
import DropItemOverlay from '../DropItemOverlay';
import CollectionDescriptionIcon from './DescriptionIcon';
import CollectionSyncedIcon from './SyncedIcon';
import TrashRemainingDays from '../TrashRemainingDays';

import {
  isArchive,
  isEllipsisActive,
  isTrash,
} from '../../helpers/App';
import { thumbnailPlaceholder } from '../../helpers/Collections';
import {
  collectionsTarget,
  collectionsSource,
} from '../../helpers/DNDCollections';
import ItemTypes from '../../helpers/DNDItemTypes';
import { getSetting } from '../../helpers/Settings';

class CollectionsGridItem extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      mounted: false,
    };

    this.elRef = React.createRef();
  }

  componentDidMount() {
    const { connectDragPreview } = this.props;

    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true,
      });
    }

    this.setState({ mounted: true });
  }

  getCollectionUrl() {
    const {
      collection,
      share,
      collectionUrlResolver,
    } = this.props;

    if (typeof collectionUrlResolver === 'function') {
      const resolvedUrl = collectionUrlResolver(collection);
      if (resolvedUrl) {
        return resolvedUrl;
      }
    }

    let url = `/collections/${collection.uuid}`;
    if (isArchive()) {
      url = `/archive${url}`;
    }
    if (isTrash()) {
      url = `/trash${url}`;
    }
    if (share) {
      url = `/shares/${share.uuid}${url}`;
    }

    return url;
  }

  handleCollectionClick(collection) {
    const { getCollectionAction } = this.props;
    getCollectionAction(collection);
  }

  render() {
    const { mounted } = this.state;
    const {
      intl,
      collection,
      selectedCollections,
      share,
      selectCollection,
      unselectCollection,
      connectDragSource,
      connectDropTarget,
      isDragging,
      isMoved,
      isOver,
      canDrop,
      reorderingMode,
      allowCheck,
      displayOnly,
      renderDisplayAction,
      selectedView,
    } = this.props;
    const featuredFile = collection.featured_file;
    const isSelected = !!selectedCollections.find(sf => (sf.uuid === collection.uuid));
    const subCollections = collection.child_collections_count || 0;

    const isFramed = selectedView === 'framed';

    const getSrc = () => {
      if (isFramed) {
        return featuredFile.urls.thumbnailmasonry;
      }

      return featuredFile.urls.thumbnail;
    };

    const getSrcSet = () => {
      if (isFramed) {
        return `${featuredFile.urls.thumbnailmasonry} 1x`;
      }

      return `${featuredFile.urls.thumbnail} 1x, ${featuredFile.urls.thumbnailx2} 2x`;
    };

    return connectDragSource(connectDropTarget((
      <div
        id={collection.uuid}
        className={classnames(
          'collections-item',
          'col-sm-12',
          {
            dragging: isDragging,
            hovered: isOver,
            'collections-item-framed': isFramed,
          },
        )}
      >
        {!displayOnly && (
          <GridItemLikes
            collection={collection}
            share={share}
            readOnly={!!collection.deleted_at}
          />
        )}
        {(allowCheck && (!share || (share && share.allow_download))) && (
          <Input
            type="checkbox"
            className="input-check check-l"
            id={`collection-${collection.uuid}`}
            checked={isSelected}
            onChange={() => {
              if (isSelected) {
                unselectCollection(collection.uuid);
              } else {
                selectCollection(collection);
              }
            }}
            aria-label={isSelected ? intl.formatMessage({ defaultMessage: 'Unselect' }) : intl.formatMessage({ defaultMessage: 'Select' })}
          />
        )}
        {isMoved && (
          <span className="grid-item-moved-icon">
            <i className="fas fa-sync fa-spin" />
          </span>
        )}
        <div>
          <Link
            to={this.getCollectionUrl()}
            className="collections-item-img-wrapper"
            onClick={() => { this.handleCollectionClick(collection); }}
          >
            {featuredFile ? (
              <img
                className="collections-item-img"
                {...(!isFramed ? { width: '100%' } : {})}
                src={getSrc()}
                srcSet={getSrcSet()}
                alt={collection.name}
              />
            ) : (
              <img
                className="collections-item-img"
                width="100%"
                src={thumbnailPlaceholder}
                alt={collection.name}
              />
            )}
            {(!share && (collection.files_count > 0 || subCollections > 0) && !getSetting('hide_collection_file_count')) && (
              <div className="collection-files-count">
                {subCollections > 0 && (
                  <span>
                    <i className="far fa-layer-group" />{' '}
                    {subCollections}
                  </span>
                )}
                {collection.files_count > 0 && (
                  <span>
                    <i className="far fa-file" />{' '}
                    {collection.files_count}
                  </span>
                )}
              </div>
            )}
          </Link>
          <div className="collections-item-body">
            {(!displayOnly && !share && !getSetting('cs1')) && (
              <Avatar
                id={`collection-${collection.uuid}`}
                className="collection-author"
                user={collection.user}
                size={30}
                tooltip={true}
                expandable={true}
              />
            )}
            <h5 className="collections-item-title" id={`collection-title-${collection.uuid}`}>
              <Link
                to={this.getCollectionUrl()}
                title={collection.name}
                onClick={() => { this.handleCollectionClick(collection); }}
                ref={this.elRef}
              >
                {collection.smart && <span className="text-glow-violet mr-2"><i className="fas fa-bolt" /></span>}
                {collection.name}
              </Link>
            </h5>
            {(mounted && isEllipsisActive(this.elRef.current)) && (
              <UncontrolledPopover
                className="file-popover"
                trigger="hover"
                fade={false}
                placement="top"
                positionFixed={true}
                target={`collection-title-${collection.uuid}`}
              >
                <PopoverBody>
                  <h5 className="masonry-item-title">
                    <Link
                      to={this.getCollectionUrl()}
                      title={collection.name}
                      onClick={() => { this.handleCollectionClick(collection); }}
                    >
                      {collection.name}
                    </Link>
                  </h5>
                </PopoverBody>
              </UncontrolledPopover>
            )}
            {(!displayOnly && !share) ? (
              <div className="collections-item-details collection-details">
                <div>
                  <CollectionsAccess collection={collection} />
                  <CollectionsDate
                    collection={collection}
                  />
                  <CollectionSyncedIcon collection={collection} />
                </div>
                <div>
                  <CollectionDescriptionIcon
                    collection={collection}
                    delay={mounted && isEllipsisActive(this.elRef.current)}
                  />
                  <TrashRemainingDays
                    collection={collection}
                  />
                </div>
              </div>
            ) : (
              <div className="collections-item-details collection-details">
                <div>
                  <CollectionDescriptionIcon
                    collection={collection}
                    delay={mounted && isEllipsisActive(this.elRef.current)}
                  />
                </div>
                <div />
              </div>
            )}
            {!displayOnly && (
              <div className="grid-item-actions">
                <CollectionsActions
                  collection={collection}
                  share={share}
                />
              </div>
            )}
            {displayOnly && typeof renderDisplayAction === 'function' && (
              <div className="collection-actions">
                <ul className="collection-actions-list">
                  {renderDisplayAction(collection, 'grid')}
                </ul>
              </div>
            )}
          </div>
          {!displayOnly && (
            <DropItemOverlay
              isOver={isOver}
              canDrop={canDrop}
              reordering={reorderingMode}
            />
          )}
        </div>
      </div>
    )));
  }
}

const mapStateToProps = state => ({
  selectedCollections: state.Collections.selectedCollections,
});

const mapDispatchToProps = dispatch => ({
  getCollectionAction: (collection) => {
    dispatch(getCollection(collection));
  },
  selectCollection: (collection) => {
    dispatch(addSelectedCollection(collection));
  },
  unselectCollection: (collectionUuid) => {
    dispatch(removeSelectedCollection(collectionUuid));
  },
});

CollectionsGridItem.propTypes = {
  intl: PropTypes.object.isRequired,
  selectedCollections: PropTypes.array.isRequired,
  collection: PropTypes.object.isRequired,
  getCollectionAction: PropTypes.func.isRequired,
  selectCollection: PropTypes.func.isRequired,
  unselectCollection: PropTypes.func.isRequired,
  share: PropTypes.object,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isMoved: PropTypes.bool.isRequired,
  isOver: PropTypes.bool.isRequired,
  canDrop: PropTypes.bool.isRequired,
  reorderingMode: PropTypes.bool,
  allowCheck: PropTypes.bool,
  displayOnly: PropTypes.bool,
  collectionUrlResolver: PropTypes.func,
  renderDisplayAction: PropTypes.func,
  selectedView: PropTypes.string,
};

CollectionsGridItem.defaultProps = {
  share: null,
  reorderingMode: false,
  allowCheck: true,
  displayOnly: false,
  collectionUrlResolver: null,
  renderDisplayAction: null,
  selectedView: null,
};

export default
injectIntl(connect(mapStateToProps, mapDispatchToProps)(
  DragSource(ItemTypes.COLLECTION, collectionsSource, (DNDconnect, monitor) => ({
    connectDragSource: DNDconnect.dragSource(),
    connectDragPreview: DNDconnect.dragPreview(),
    isDragging: monitor.isDragging(),
  }))(DropTarget([
    ItemTypes.COLLECTION,
    ItemTypes.FILE,
  ], collectionsTarget, (DNDconnect, monitor) => ({
    connectDropTarget: DNDconnect.dropTarget(),
    isOver: monitor.isOver(),
    canDrop: monitor.canDrop(),
  }))(CollectionsGridItem)),
));
